import type {
  DocumentSectionSpec,
  SectionOption,
  StoredSection,
  StoredSectionOption,
} from '../config/types';

export const storedOptionToWire = (
  option: StoredSectionOption,
): SectionOption => ({
  Text: option.text,
  Slots: Object.fromEntries(
    Object.entries(option.slots ?? {}).map(([slot, values]) => [
      slot,
      { Values: values },
    ]),
  ),
  Instructions: option.instructions,
  SlotInstructions: { ...option.slot_instructions },
});

// Empty strings are left to scribe's write defaults. The stored shape reads
// a missing `required` as true, as scribe reads its own documents, because
// the wire flag cannot be absent.
export const storedSectionToWire = (
  section: StoredSection,
): DocumentSectionSpec => ({
  ID: section.id ?? '',
  Code: section.code,
  Title: section.title,
  Category: section.category,
  EditorType: section.editor_type ?? '',
  Type: section.type ?? '',
  ProcessingType: section.processing_type?.toLowerCase() ?? '',
  Instructions: section.instructions,
  Options: (section.options ?? []).map(storedOptionToWire),
  TemplateType: section.template_type ?? '',
  Required: section.required !== false,
  Lines: [],
});

// The editor of a clinic without Transcriptor v2 sends a section without a
// `required` value as optional, as it did before.
export const classicSectionToWire = (
  section: StoredSection,
): DocumentSectionSpec =>
  storedSectionToWire({ ...section, required: section.required === true });

const emptyToUndefined = <T extends Record<string, unknown>>(
  value: T,
): T | undefined => (Object.keys(value).length > 0 ? value : undefined);

const wireOptionToStored = (option: SectionOption): StoredSectionOption => ({
  text: option.Text,
  slots: emptyToUndefined(
    Object.fromEntries(
      Object.entries(option.Slots).map(([slot, values]) => [
        slot,
        values.Values,
      ]),
    ),
  ),
  instructions: option.Instructions,
  slot_instructions: emptyToUndefined({ ...option.SlotInstructions }),
});

export const wireSectionToStored = (
  section: DocumentSectionSpec,
): StoredSection => ({
  id: section.ID || undefined,
  code: section.Code,
  title: section.Title,
  category: section.Category,
  editor_type: (section.EditorType ||
    undefined) as StoredSection['editor_type'],
  type: section.Type || undefined,
  processing_type: (section.ProcessingType.toUpperCase() ||
    undefined) as StoredSection['processing_type'],
  instructions: section.Instructions,
  options: section.Options.map(wireOptionToStored),
  template_type: (section.TemplateType ||
    undefined) as StoredSection['template_type'],
  required: section.Required,
});
